import { BookOpen, Check, CheckCircle2, Gauge, ShieldCheck } from "lucide-react";
import { useId } from "react";
import type { Locale } from "./i18n";
import type { ExperienceMode } from "./experience-mode";
import { createExperienceModeTranslator } from "./experience-mode-i18n";
import "./experience-mode.css";

interface ExperienceModeSelectorProps {
  mode: ExperienceMode;
  locale: Locale;
  onChange: (nextMode: ExperienceMode) => void;
  compact?: boolean;
}

interface GuidedLearningCardProps {
  title: string;
  items: readonly string[];
  tone?: "blue" | "orange";
}

/**
 * Chooses the amount of explanatory UI without changing any backtest input,
 * strategy, result, account entitlement, or saved data.
 */
export function ExperienceModeSelector({
  mode,
  locale,
  onChange,
  compact = false,
}: ExperienceModeSelectorProps) {
  const t = createExperienceModeTranslator(locale);
  const titleID = useId();
  const detailID = useId();
  const invariantID = useId();

  const options: ReadonlyArray<{
    value: ExperienceMode;
    name: string;
    description: string;
    icon: typeof BookOpen;
    recommended?: boolean;
  }> = [
    {
      value: "guided",
      name: t("guidedName"),
      description: t("guidedDescription"),
      icon: BookOpen,
      recommended: true,
    },
    {
      value: "focused",
      name: t("focusedName"),
      description: t("focusedDescription"),
      icon: Gauge,
    },
  ];

  return (
    <section
      className={`experience-mode-selector${compact ? " experience-mode-selector--compact" : ""}`}
      aria-labelledby={titleID}
      aria-describedby={`${detailID} ${invariantID}`}
    >
      <header className="experience-mode-selector__header">
        <p className="experience-mode-selector__eyebrow">{t("experienceTitle")}</p>
        <h2 id={titleID}>{t("onboardingQuestion")}</h2>
        <p id={detailID}>{t("onboardingDetail")}</p>
      </header>

      <div
        className="experience-mode-selector__options"
        role="radiogroup"
        aria-labelledby={titleID}
        aria-describedby={`${detailID} ${invariantID}`}
      >
        {options.map((option, optionIndex) => {
          const selected = mode === option.value;
          const Icon = option.icon;

          return (
            <button
              key={option.value}
              className={`experience-mode-option${selected ? " experience-mode-option--selected" : ""}`}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(option.value)}
              onKeyDown={(event) => {
                let nextIndex: number | undefined;

                switch (event.key) {
                  case "ArrowRight":
                  case "ArrowDown":
                    nextIndex = (optionIndex + 1) % options.length;
                    break;
                  case "ArrowLeft":
                  case "ArrowUp":
                    nextIndex = (optionIndex - 1 + options.length) % options.length;
                    break;
                  case "Home":
                    nextIndex = 0;
                    break;
                  case "End":
                    nextIndex = options.length - 1;
                    break;
                  default:
                    return;
                }

                if (nextIndex === undefined) return;
                event.preventDefault();
                onChange(options[nextIndex].value);
                const radios = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                  '[role="radio"]',
                );
                radios?.[nextIndex]?.focus();
              }}
            >
              <span className="experience-mode-option__icon" aria-hidden="true">
                <Icon size={compact ? 18 : 21} strokeWidth={2} />
              </span>
              <span className="experience-mode-option__copy">
                <span className="experience-mode-option__heading">
                  <strong>{option.name}</strong>
                  {option.recommended && (
                    <span className="experience-mode-option__badge">
                      {t("recommended")}
                    </span>
                  )}
                </span>
                <span className="experience-mode-option__description">
                  {option.description}
                </span>
              </span>
              <span className="experience-mode-option__control" aria-hidden="true">
                {selected && <Check size={14} strokeWidth={3} />}
              </span>
            </button>
          );
        })}
      </div>

      <div className="experience-mode-selector__invariant" id={invariantID}>
        <ShieldCheck size={16} aria-hidden="true" />
        <span>{t("interfaceStyleDetail")}</span>
      </div>
      {!compact && (
        <p className="experience-mode-selector__change-note">
          {t("changeAnytime")}
        </p>
      )}
    </section>
  );
}

/** A reusable, concise teaching card rendered only by Guided experiences. */
export function GuidedLearningCard({
  title,
  items,
  tone = "blue",
}: GuidedLearningCardProps) {
  const titleID = useId();

  return (
    <aside
      className={`guided-learning-card guided-learning-card--${tone}`}
      aria-labelledby={titleID}
    >
      <span className="guided-learning-card__icon" aria-hidden="true">
        <BookOpen size={19} strokeWidth={2} />
      </span>
      <div className="guided-learning-card__content">
        <h3 id={titleID}>{title}</h3>
        <ul>
          {items.map((item, index) => (
            <li key={`${index}-${item}`}>
              <CheckCircle2 size={15} aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

export type { ExperienceModeSelectorProps, GuidedLearningCardProps };
